import { useState } from 'react';

const serviceCategories = [
  { id: 'all', name: 'All Projects' },
  { id: 'framing', name: 'Framing' },
  { id: 'roofing', name: 'Roofing' },
  { id: 'siding', name: 'Siding' },
  { id: 'remodeling', name: 'Remodeling' }
];

const projects = [
  {
    id: 1,
    category: 'framing',
    title: 'Modern Home Framing',
    location: 'Watertown, CT',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/ec62d147a5ef4b2bcce0dade400229e8.png'
  },
  {
    id: 3,
    category: 'roofing',
    title: 'Residential Roof Installation',
    location: 'Watertown, CT',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/605ecf04bcb4613f54e496f0c448e944.png'
  },
  {
    id: 4,
    category: 'siding',
    title: 'Vinyl Siding Project',
    location: 'Danbury, CT',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/2d36a36129f282f328e333409870e20e.jpeg'
  },
  {
    id: 7,
    category: 'framing',
    title: 'Custom Home Structure',
    location: 'Tolland, CT',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/95491318f684163c6583caa08f331263.jpeg'
  },
  {
    id: 9,
    category: 'remodeling',
    title: 'School Renovation',
    location: 'Terryville, CT',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/be51f2c16e324df9366888aeef25f70b.jpeg'
  },
  {
    id: 10,
    category: 'siding',
    title: 'Cedar Shake Installation',
    location: 'Vernon, CT',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/5bac9e6d6dae5a8b459b2d0e3da04d51.png'
  }
];

export default function ServiceGallery() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState<typeof projects[0] | null>(null);

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="gallery" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-brand text-sm font-semibold uppercase tracking-wider">
            Our Portfolio
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-4">
            Completed Projects Gallery
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Browse our collection of successfully completed projects across all service categories
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {serviceCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === category.id
                  ? 'bg-brand text-white shadow-lg shadow-brand/30'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedProject(project)}
            >
              <div className="relative w-full h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="inline-flex items-center text-white text-sm font-medium">
                    <i className="ri-eye-line mr-2"></i>
                    View Project
                  </span>
                </div>
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-brand/10 text-brand text-xs font-semibold rounded-full mb-3 capitalize">
                  {project.category}
                </span>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-brand transition-colors">
                  {project.title}
                </h3>
                <div className="flex items-center text-gray-500 text-sm">
                  <i className="ri-map-pin-line mr-2"></i>
                  {project.location}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-6">
            Ready to start your project? Let&apos;s discuss your vision.
          </p>
          <button
            onClick={scrollToContact}
            className="bg-brand text-white px-10 py-4 rounded-xl font-semibold hover:bg-brand-dark transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Your Free Estimate
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-80 md:h-96">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover object-top"
              />
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/90 rounded-full hover:bg-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-800"></i>
              </button>
            </div>
            <div className="p-8">
              <span className="inline-block px-3 py-1 bg-brand/10 text-brand text-xs font-semibold rounded-full mb-3 capitalize">
                {selectedProject.category}
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedProject.title}
              </h3>
              <div className="flex items-center text-gray-500 mb-6">
                <i className="ri-map-pin-line mr-2"></i>
                {selectedProject.location}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setSelectedProject(null);
                    scrollToContact();
                  }}
                  className="bg-brand text-white px-8 py-3 rounded-xl font-semibold hover:bg-brand-dark transition-all whitespace-nowrap cursor-pointer"
                >
                  Request Similar Project
                </button>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="bg-gray-100 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
